"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, ArrowDown, Compass, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CosmicOrb } from "@/components/cosmic/CosmicOrb";
import styles from "../YourInfiniteSelves.module.css";
const SCENARIOS = [{
  title: "A Different Life Path",
  description: "One early crossroads taken another way. Grounded, human, bittersweet.",
  glyph: "Δ"
}, {
  title: "An Advanced Future Civilization",
  description: "Aeons ahead in a cosmos where energy and information are mastered.",
  glyph: "Ψ"
}, {
  title: "A Radically Different World",
  description: "Altered physical constants, strange gravity, and alien geometry.",
  glyph: "Ω"
}, {
  title: "An Unexpected Identity",
  description: "A profound reversal of strengths, revealing a startling alternate role.",
  glyph: "✦"
}];
export default function IntroHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="intro-hero" data-nav="dark" className="relative w-full max-w-full overflow-hidden bg-transparent text-foreground pt-20 pb-16 sm:pt-24 sm:pb-20 md:pt-32 md:pb-28 border-b border-border">
      {/* Background starlight-grid mask texture */}
      <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${styles.starlightGrid} opacity-40`} />

      {/* CosmicOrb cluster: ambient celestial spheres framing the hero */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-8 left-0 sm:-top-12 sm:left-4 lg:left-12 opacity-50 sm:opacity-65">
        <div className="scale-75 sm:scale-100 origin-top-left">
          <CosmicOrb size={180} hue="violet" rings={2} />
        </div>
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute top-20 right-0 sm:top-28 sm:right-6 lg:right-16 opacity-60 sm:opacity-75">
        <div className="scale-75 sm:scale-100 origin-top-right">
          <CosmicOrb size={160} hue="cyan" rings={1} />
        </div>
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute bottom-8 left-1/4 opacity-40 hidden md:block">
        <CosmicOrb size={100} hue="blue" rings={1} />
      </div>

      <div className="relative z-10 mx-auto max-w-[76rem] px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex flex-col items-center text-center">
          {/* Celestial Signature: dual hairline rule with central cyan diamond node */}
          <div aria-hidden="true" className="flex items-center justify-center gap-3 w-full max-w-xs mb-5 opacity-80">
            <div className="h-px flex-1 bg-border" />
            <div className="size-2 rotate-45 border border-primary bg-primary/20 shadow-xs" />
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Eyebrow */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="ql-eyebrow mb-3 break-words">
            Fictional multiverse storytelling
          </motion.p>

          {/* Display H1 Heading */}
          <motion.h1 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-foreground leading-[1.08] max-w-4xl break-words">
            Your Infinite Selves
          </motion.h1>

          {/* Tagline */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className={`font-serif text-lg sm:text-2xl md:text-3xl font-semibold mt-3 sm:mt-4 mb-4 sm:mb-6 tracking-wide break-words ${styles.shimmerText}`}>
            One Identity. Infinite Possibilities.
          </motion.p>

          {/* Descriptive Intro Body */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="font-sans text-sm sm:text-lg md:text-xl text-foreground/85 max-w-2xl leading-relaxed mb-6 font-normal break-words">
            Pick a nickname, answer a few optional questions, and explore four imagined versions of you living in four imagined universes. The full Multiverse Life Dossier and downloadable PDF are completely free to explore.
          </motion.p>

          {/* High-visibility Glass Persistent Fiction Notice */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="ql-glass-strong inline-flex items-start sm:items-center gap-2.5 sm:gap-3 rounded-2xl sm:rounded-full p-3 sm:px-5 sm:py-2.5 text-xs sm:text-sm text-foreground/95 max-w-2xl mb-8 border border-primary/40 shadow-lg">
            <ShieldAlert className="size-4 shrink-0 text-primary mt-0.5 sm:mt-0" aria-hidden="true" />
            <span className="font-sans leading-normal text-left break-words">
              <strong className="text-primary font-semibold">Imaginative Multiverse Fiction: </strong>
              Science-inspired storytelling generated purely in your browser — not proven parallel universes, and not real predictions.
            </span>
          </motion.div>

          {/* Branching Timelines Animation: a glowing thread splitting into four paths that gently pulse */}
          <div aria-hidden="true" className="relative my-4 w-full max-w-2xl h-32 sm:h-44 md:h-52 flex items-center justify-center pointer-events-none select-none overflow-hidden">
            <svg viewBox="0 0 800 220" fill="none" xmlns="http://www.w3.org/2000/svg" className={`w-full h-full max-w-full ${styles.timelineArcPulse}`} preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="arcGlow1" x1="400" y1="190" x2="100" y2="40" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="arcGlow2" x1="400" y1="190" x2="300" y2="30" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="hsl(215 100% 60%)" stopOpacity="0.4" />
                </linearGradient>
                <linearGradient id="arcGlow3" x1="400" y1="190" x2="500" y2="30" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="hsl(270 70% 55%)" stopOpacity="0.4" />
                </linearGradient>
                <linearGradient id="arcGlow4" x1="400" y1="190" x2="700" y2="40" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="var(--chart-3)" stopOpacity="0.4" />
                </linearGradient>
                <radialGradient id="originCore" cx="400" cy="190" r="30" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="var(--foreground)" />
                  <stop offset="40%" stopColor="var(--primary)" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Background faint guides */}
              <circle cx="400" cy="190" r="160" stroke="var(--border)" strokeDasharray="3 6" strokeWidth="1" />
              <circle cx="400" cy="190" r="110" stroke="var(--border)" strokeDasharray="2 4" strokeWidth="1" />

              {/* Arc 1: Divergent (Left wide) */}
              <path d="M 400 190 C 350 140, 220 90, 110 50" stroke="url(#arcGlow1)" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="110" cy="50" r="4" fill="var(--primary)" />
              <circle cx="110" cy="50" r="9" stroke="var(--primary)" strokeOpacity="0.4" strokeWidth="1" />

              {/* Arc 2: Ascendant (Left high) */}
              <path d="M 400 190 C 370 120, 330 70, 300 35" stroke="url(#arcGlow2)" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="300" cy="35" r="4" fill="hsl(215 100% 60%)" />
              <circle cx="300" cy="35" r="9" stroke="hsl(215 100% 60%)" strokeOpacity="0.4" strokeWidth="1" />

              {/* Arc 3: Strange (Right high) */}
              <path d="M 400 190 C 430 120, 470 70, 500 35" stroke="url(#arcGlow3)" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="500" cy="35" r="4" fill="hsl(270 70% 55%)" />
              <circle cx="500" cy="35" r="9" stroke="hsl(270 70% 55%)" strokeOpacity="0.4" strokeWidth="1" />

              {/* Arc 4: Unexpected (Right wide) */}
              <path d="M 400 190 C 450 140, 580 90, 690 50" stroke="url(#arcGlow4)" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="690" cy="50" r="4" fill="var(--chart-3)" />
              <circle cx="690" cy="50" r="9" stroke="var(--chart-3)" strokeOpacity="0.4" strokeWidth="1" />

              {/* Origin convergence point */}
              <circle cx="400" cy="190" r="28" fill="url(#originCore)" />
              <circle cx="400" cy="190" r="5" fill="var(--foreground)" />
              <circle cx="400" cy="190" r="11" stroke="var(--primary)" strokeWidth="1.5" />
            </svg>
          </div>

          {/* Action CTAs */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.35
        }} className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-4 w-full sm:w-auto">
            <Link to="#selves-studio" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center text-center">
              <span>Meet my other selves</span>
              <ArrowDown className="size-4 shrink-0 ml-1.5" />
            </Link>

            <Link to="/CreateYourUniversePage" className="ql-btn-ghost w-full sm:w-auto min-h-[44px] justify-center text-center">
              <Compass className="size-4 shrink-0 text-primary mr-1.5" />
              <span>Design a universe first</span>
            </Link>
          </motion.div>

          {/* Four Archetype Teaser Cards */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.45
        }} className="mt-12 w-full max-w-5xl">
            <div className="text-center mb-4">
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Four Fictional Lives Generated Together
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-left">
              {SCENARIOS.map((item, idx) => <div key={idx} className="ql-card p-4 transition-colors">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-serif font-bold text-xs text-primary">{item.glyph}</span>
                    <h2 className="font-serif text-sm font-semibold text-card-foreground leading-snug">
                      {item.title}
                    </h2>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}